import { type ReactElement, useEffect, useState } from "react";
import { AlertTriangle, CloudOff, RefreshCw, RotateCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useOfflineQueue } from "./OfflineProvider";
import { dismissItem, listFailed, retryAllFailed, retryItem } from "./queue";
import type { OpKind, QueueItem } from "./types";

/**
 * Compact status pill for the app header. Invisible on the happy path. When
 * writes have permanently failed it becomes a tappable "Sync problems" sheet
 * with a reason + retry/dismiss per item — no more count-only pill that falsely
 * promises a retry.
 */
export function OfflineIndicator({ className = "" }: { className?: string }) {
  const { online, pending, failed, flushing } = useOfflineQueue();

  if (online && pending === 0 && failed === 0) return null;

  if (online && failed > 0) {
    return <SyncProblems failed={failed} className={className} />;
  }

  const offline = !online;
  const tone = offline
    ? "text-warning bg-warning/10"
    : "text-muted-foreground bg-foreground/[0.06]";
  const icon: ReactElement = offline ? (
    <CloudOff className="h-3.5 w-3.5" />
  ) : (
    <RefreshCw className={`h-3.5 w-3.5 ${flushing ? "animate-spin" : ""}`} />
  );
  const label = offline
    ? pending > 0
      ? `Offline · ${pending} queued`
      : "Offline"
    : `Syncing ${pending}…`;

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${tone} ${className}`}
      role="status"
      aria-live="polite"
      title={
        offline
          ? "You're offline. Your actions are saved and will sync automatically."
          : "Syncing your offline actions…"
      }
    >
      {icon}
      <span>{label}</span>
    </span>
  );
}

const OP_LABELS: Record<OpKind, string> = {
  metric_entry: "Metric entry",
  checkin: "Check-in",
  break_toggle: "Break",
  checkout: "Check-out",
  submit_log: "Hours submission",
  geofence_violation: "Location alert",
  checkin_photo: "Check-in photo",
};

function whenLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function SyncProblems({ failed, className }: { failed: number; className: string }) {
  const [items, setItems] = useState<QueueItem[]>([]);

  // Reload the list whenever the failed count changes (retry/dismiss update it).
  useEffect(() => {
    let active = true;
    void listFailed().then((list) => {
      if (active) setItems(list);
    });
    return () => {
      active = false;
    };
  }, [failed]);

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-danger bg-danger/10 hover:bg-danger/20 transition-colors ${className}`}
          aria-label={`${failed} action${failed === 1 ? "" : "s"} didn't sync — tap to review`}
        >
          <AlertTriangle className="h-3.5 w-3.5" />
          <span>{failed} didn't sync</span>
        </button>
      </SheetTrigger>
      <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Sync problems</SheetTitle>
          <SheetDescription>
            These actions couldn't be saved to the server. Retry them when you have a good
            connection, or dismiss ones you no longer need.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-2">
          {items.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">All caught up.</p>
          ) : (
            items.map((item) => (
              <div key={item.id} className="rounded-xl border border-border bg-card p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {OP_LABELS[item.kind] ?? item.kind}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {whenLabel(item.createdAt)}
                    </p>
                    {item.lastError && (
                      <p className="mt-1 break-words text-xs text-danger">
                        {item.lastError}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button
                      variant="outline"
                      className="h-8 gap-1 px-2.5 text-xs"
                      onClick={() => void retryItem(item.id)}
                    >
                      <RotateCw className="h-3.5 w-3.5" /> Retry
                    </Button>
                    <Button
                      variant="ghost"
                      className="h-8 w-8 p-0 text-muted-foreground"
                      aria-label="Dismiss"
                      onClick={() => void dismissItem(item.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 1 && (
          <div className="mt-4">
            <Button variant="accent" className="w-full gap-2" onClick={() => void retryAllFailed()}>
              <RotateCw className="h-4 w-4" /> Retry all
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
